import { useEffect, useRef, useState, type ReactNode } from "react";

import { dismissOfferChanges, undismissOfferChanges } from "../lib/offerChanges";

const UNDO_MS = 5000;

/**
 * Dismissing what a run added, on Refresh and the Offers page alike: hidden at
 * once, saved in the background to the same `dismissed_changes` as every other
 * change, so it leaves both pages; Undo brings it back.
 */
export function useAddDismissals(onError: (message: string) => void): {
  isHidden: (key: string) => boolean;
  hide: (keys: string[]) => void;
  toast: ReactNode;
} {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [undo, setUndo] = useState<string[] | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // The last dismiss in flight, so an Undo reaches the server after it.
  const pending = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => () => clearTimeout(timer.current), []);

  const without = (keys: string[]) => (h: Set<string>) =>
    new Set([...h].filter((k) => !keys.includes(k)));

  const hide = (keys: string[]) => {
    if (keys.length === 0) return;
    setHidden((h) => new Set([...h, ...keys]));
    setUndo(keys);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setUndo(null), UNDO_MS);
    pending.current = dismissOfferChanges(keys).catch((e: unknown) => {
      setHidden(without(keys));
      onError(e instanceof Error ? e.message : String(e));
    });
  };

  const restore = () => {
    if (!undo) return;
    const keys = undo;
    setUndo(null);
    clearTimeout(timer.current);
    setHidden(without(keys));
    pending.current.then(() => undismissOfferChanges(keys)).catch((e: unknown) => {
      setHidden((h) => new Set([...h, ...keys]));
      onError(e instanceof Error ? e.message : String(e));
    });
  };

  const toast = undo && (
    <div
      role="status"
      className="fixed bottom-[calc(20px+env(safe-area-inset-bottom,0px))] left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-neutral-100 py-2 pl-3.5 pr-2 text-sm font-semibold text-neutral-950"
    >
      <span>{undo.length === 1 ? "Dismissed" : `Dismissed ${undo.length}`}</span>
      <button type="button" onClick={restore} className="px-1.5 py-1 font-bold text-blue-700">
        Undo
      </button>
    </div>
  );

  return { isHidden: (key) => hidden.has(key), hide, toast };
}
